import createClient from "openapi-fetch";
import { apiBaseUrl } from "./config";
import type { paths } from "./schema";

/* Every response is `{ data: … }` on success and
   `{ error: { code, message, details } }` on failure, where `details` carries
   the raw validation issues on a 400 and `{}` on 401/403. */

export const REQUEST_TIMEOUT_MS = 8000;

export type PlatformErrorBody = {
  error: { code: number; message: string; details?: unknown };
};

/* Callers branch on the kind, not on a status number, because a timeout and a
   500 need the same treatment while a 401 and a 403 do not. */
export type PlatformErrorKind =
  | "unauthorized" /* 401 — no session */
  | "forbidden" /* 403 — origin not allowlisted, or geoblocked */
  | "not-found" /* 404 */
  | "invalid" /* 400 — our request was wrong */
  | "conflict" /* 409 */
  | "rate-limited" /* 429 */
  | "upstream" /* 5xx from the API */
  | "timeout" /* the API did not answer in time */
  | "network"; /* DNS, TLS, connection refused */

export const kindForStatus = (status: number): PlatformErrorKind => {
  if (status === 400) {
    return "invalid";
  }

  if (status === 401) {
    return "unauthorized";
  }

  if (status === 403) {
    return "forbidden";
  }

  if (status === 404) {
    return "not-found";
  }

  if (status === 409) {
    return "conflict";
  }

  if (status === 429) {
    return "rate-limited";
  }

  return "upstream";
};

export class PlatformError extends Error {
  readonly status: number;
  readonly kind: PlatformErrorKind;
  readonly details: unknown;

  constructor(status: number, kind: PlatformErrorKind, message: string, details?: unknown) {
    super(message);
    this.name = "PlatformError";
    this.status = status;
    this.kind = kind;
    this.details = details;
  }

  /* True where retrying could plausibly succeed. A 403 never will. */
  get isTransient() {
    return this.kind === "timeout" || this.kind === "network" || this.kind === "upstream";
  }
}

export const isPlatformErrorBody = (value: unknown): value is PlatformErrorBody => {
  if (typeof value !== "object" || value === null || !("error" in value)) {
    return false;
  }

  const error = (value as { error: unknown }).error;

  return typeof error === "object" && error !== null && "code" in error && "message" in error;
};

/* A hanging upstream must not hang a server render, so every request carries a
   deadline. A caller's own signal still wins if it aborts first. */
const withTimeout = (input: Request | URL | string, init?: RequestInit): Promise<Response> => {
  const timeout = AbortSignal.timeout(REQUEST_TIMEOUT_MS);
  const signal = init?.signal ? AbortSignal.any([init.signal, timeout]) : timeout;
  const next = input instanceof Request ? (input as Request & { next?: RequestInit["next"] }).next : undefined;

  return fetch(input, { ...init, ...(next ? { next } : {}), signal }).catch((cause) => {
    if (timeout.aborted) {
      throw new PlatformError(504, "timeout", `The API did not answer within ${REQUEST_TIMEOUT_MS}ms.`, cause);
    }

    if (cause instanceof PlatformError) {
      throw cause;
    }

    throw new PlatformError(0, "network", "Could not reach the API.", cause);
  });
};

export const createPlatformClient = (siteToken: string) =>
  createClient<paths>({ baseUrl: apiBaseUrl(siteToken), fetch: withTimeout });

/* openapi-fetch hands back `{ data, error, response }` where `data` is the
   whole envelope. Flatten it so callers never see two levels of `data`, and
   turn every failure into one error type. */
export const unwrap = <T extends { data: unknown }>(result: {
  data?: T;
  error?: unknown;
  response: Response;
}) => {
  if (result.data !== undefined) {
    return result.data;
  }

  const status = result.response?.status ?? 0;
  const kind = status === 0 ? "network" : kindForStatus(status);

  if (isPlatformErrorBody(result.error)) {
    throw new PlatformError(status, kind, result.error.error.message, result.error.error.details);
  }

  throw new PlatformError(status, kind, `Request failed with ${status}.`, result.error);
};

/* Never throws. For the many places where one failing section should not take
   a page down with it. */
export const attempt = async <T>(work: Promise<T>) => {
  try {
    return { ok: true as const, value: await work };
  } catch (cause) {
    const error =
      cause instanceof PlatformError
        ? cause
        : new PlatformError(0, "network", cause instanceof Error ? cause.message : "Unknown failure.", cause);

    return { ok: false as const, error };
  }
};
